import React from 'react'
import { Box, Tooltip, Typography } from '@mui/material'

const clean = value => (typeof value === 'string' ? value.trim() : '')

/**
 * The note lines for one side of an order.
 *
 * The server mirrors each pair on create — orderNotes falls back to
 * instructions, riderNotes to deliveryNotes — so only the first non-empty value
 * of a pair is read; showing both would print the same sentence twice.
 *
 * `deliveryAddress.details` is deliberately not treated as a note: it holds the
 * formatted address, which the Order Information column already shows, so
 * surfacing it here just trains the team to ignore the column.
 */
export const collectOrderNotes = (order, type) => {
  const notes = []
  const seen = new Set()

  const push = value => {
    const text = clean(value)
    if (!text) return
    const key = text.toLowerCase()
    if (seen.has(key)) return
    seen.add(key)
    notes.push(text)
  }

  if (type === 'rider') {
    // Access codes, buzzer numbers, "leave at the door".
    push(order?.riderNotes || order?.deliveryNotes)
  } else {
    // What the kitchen needs to know, including per-item requests, which are
    // missed just as often as the order-level note.
    push(order?.orderNotes || order?.instructions)
    ;(order?.items || []).forEach(item => push(item?.specialInstructions))
  }

  return notes
}

const OrderNotes = ({ order, type }) => {
  const notes = collectOrderNotes(order, type)

  if (!notes.length) {
    return (
      <Typography variant="body2" color="text.secondary">
        —
      </Typography>
    )
  }

  const full = notes.join('\n')

  return (
    <Tooltip
      title={<Box sx={{ whiteSpace: 'pre-line' }}>{full}</Box>}
      placement="left"
      arrow>
      <Box
        sx={{ overflow: 'visible', py: 0.5, textAlign: 'left', width: '100%' }}>
        {notes.map((note, index) => (
          <Typography
            key={index}
            variant="caption"
            display="block"
            sx={{
              whiteSpace: 'normal',
              wordBreak: 'break-word',
              lineHeight: 1.3
            }}>
            {note}
          </Typography>
        ))}
      </Box>
    </Tooltip>
  )
}

export default OrderNotes
